import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, MagnifyingGlass, FileText, X, UploadSimple, ArrowClockwise, Check,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { devApi } from './devApi';
import { CitationList, ConfidenceBadge } from './Citations';

const DMS = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

/**
 * アイデア → メモ・添付 → AI下書き → 確認 → 登録。
 * 自動保存はしない。書類はドキュメントアプリが所有し、ここでは参照するだけ。
 */
export const ConceptAiDraft = ({ developmentType, onApply }) => {
  const [notes, setNotes] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [folder, setFolder] = useState('');

  const search = async (v) => {
    setQ(v);
    if (!v.trim()) { setHits([]); return; }
    try {
      const r = await axios.get(`${DMS}/documents`, { params: { q: v, limit: 8 } });
      setHits(r.data || []);
    } catch (_e) { setHits([]); }
  };

  const pick = (d) => {
    if (!picked.some((p) => p.id === d.id)) setPicked([...picked, d]);
    setQ(''); setHits([]);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      let fid = folder;
      if (!fid) {
        const fs = (await axios.get(`${DMS}/folders`)).data || [];
        const list = fs.folders || fs;
        fid = (list.find((x) => x.seed_key === 'eng_projects') || list[0] || {}).id;
        setFolder(fid);
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      fd.append('folder_id', fid);
      const r = await axios.post(`${DMS}/documents/upload`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      pick(r.data);
      toast.success('ドキュメントアプリに登録し、材料に追加しました');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'アップロードに失敗しました');
    }
    setUploading(false);
  };

  const draft = async () => {
    setBusy(true);
    try {
      const r = await devApi.aiDraft({
        notes,
        document_ids: picked.map((p) => p.id),
        development_type: developmentType || null,
      });
      setResult(r);
      if (r.note) toast.error(r.note);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AIの下書きを生成できませんでした');
    }
    setBusy(false);
  };

  const applyAll = () => {
    const s = result?.suggestions || {};
    onApply(Object.fromEntries(Object.entries(s).filter(([, v]) => (v || '').trim())));
    toast.success('下書きをフォームに反映しました。内容を確認して修正してください。');
  };

  return (
    <section className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-ai-draft">
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-300 bg-zinc-100 px-4 py-2">
        <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
          <Sparkle size={15} weight="duotone" className="text-teal-700" /> AIで構想の下書きを作る（任意）
        </p>
        <p className="text-xs text-zinc-600">
          メモや書類から下書きを提案します。保存はされません。最終確認は担当者が行います。
        </p>
      </div>

      <div className="space-y-4 p-4">
        <div>
          <Label className="text-xs font-semibold text-zinc-700">メモ・アイデア</Label>
          <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="例: 既存の温度炉が±8℃ばらつく。製造2課の受入検査を標準化したい。予算は50万円程度、9月末までに欲しい。"
            data-testid="dev-ai-notes-input" />
        </div>

        <div>
          <Label className="text-xs font-semibold text-zinc-700">材料にする書類（任意）</Label>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-500" />
              <Input value={q} onChange={(e) => search(e.target.value)} className="w-64 pl-8"
                placeholder="ドキュメントアプリから探す" data-testid="dev-ai-doc-search-input" />
            </div>
            <label className="inline-flex">
              <input type="file" className="hidden" data-testid="dev-ai-upload-input"
                onChange={(e) => upload(e.target.files?.[0])} />
              <span className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50">
                <UploadSimple size={14} /> {uploading ? '登録中…' : 'ファイルを登録して使う'}
              </span>
            </label>
          </div>
          {hits.length > 0 && (
            <div className="mt-2 max-h-44 divide-y divide-zinc-200 overflow-y-auto rounded-md border border-zinc-300">
              {hits.map((d) => (
                <button key={d.id} type="button" onClick={() => pick(d)}
                  data-testid={`dev-ai-doc-option-${d.id}`}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-teal-50/60">
                  <FileText size={14} className="text-zinc-500 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-900">
                    {d.document_number ? `${d.document_number} · ` : ''}{d.title}
                  </span>
                </button>
              ))}
            </div>
          )}
          {picked.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dev-ai-picked-docs">
              {picked.map((d) => (
                <span key={d.id} data-testid={`dev-ai-picked-${d.id}`}
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-zinc-50 px-2 py-1 text-xs font-medium text-zinc-800">
                  <FileText size={12} /> {d.title}
                  <button type="button" onClick={() => setPicked(picked.filter((p) => p.id !== d.id))}
                    aria-label="外す" className="text-zinc-500 hover:text-rose-600">
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="mt-1 text-xs text-zinc-600">
            PDF・画像はOCRで文字を読み取って材料にします。書類はドキュメントアプリで管理されたままです。
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={draft} disabled={busy || (!notes.trim() && picked.length === 0)}
            data-testid="dev-ai-draft-btn">
            <Sparkle size={14} className="mr-1" /> {busy ? '作成中…' : 'AIに下書きを作らせる'}
          </Button>
          {result && (
            <Button variant="outline" onClick={draft} disabled={busy} data-testid="dev-ai-redraft-btn">
              <ArrowClockwise size={14} className="mr-1" /> もう一度
            </Button>
          )}
        </div>

        {result && (
          <div className="space-y-3 rounded-lg border border-teal-300 bg-teal-50/40 p-4" data-testid="dev-ai-result">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-bold text-zinc-900">AIの下書き</p>
              <p className="text-xs font-semibold text-teal-900">{result.disclaimer}</p>
              <Button size="sm" className="ml-auto" onClick={applyAll} data-testid="dev-ai-apply-all-btn">
                <Check size={13} className="mr-1" /> すべてフォームに反映
              </Button>
            </div>
            {result.note && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900"
                data-testid="dev-ai-note">{result.note}</p>
            )}
            {(result.sources || []).length > 0 && (
              <p className="text-xs text-zinc-700" data-testid="dev-ai-sources">
                参照した書類: {result.sources.map((s) => `${s.title}（${s.used ? `${s.chars}文字` : '文字なし'}）`).join('、')}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {(result.fields || []).map((f) => {
                const detail = (result.details || {})[f.key] || {};
                const v = detail.text || (result.suggestions || {})[f.key] || '';
                return (
                  <div key={f.key} className="rounded-md border border-zinc-300 bg-white p-3"
                    data-testid={`dev-ai-suggestion-${f.key}`}>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-bold text-zinc-800">{f.label}</p>
                      {v.trim() && <ConfidenceBadge level={detail.confidence} testid={`dev-ai-conf-${f.key}`} />}
                      <Button size="sm" variant="outline" className="ml-auto h-7 px-2 text-xs"
                        disabled={!v.trim()} onClick={() => onApply({ [f.key]: v })}
                        data-testid={`dev-ai-apply-${f.key}`}>反映</Button>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-900">
                      {v.trim() || <span className="text-zinc-500">材料からは判断できませんでした</span>}
                    </p>
                    {v.trim() && (
                      <CitationList citations={detail.citations} testid={`dev-ai-citations-${f.key}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
